import { useState, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Eye, X } from 'lucide-react';
import { PeriodSelector, validatePeriodMax12Months, type PeriodValue } from '@/shared/components/PeriodSelector.tsx';
import { DownloadButton } from '@/shared/components/DownloadButton.tsx';
import { ReportTable } from '@/shared/components/ReportTable.tsx';
import { useMaintenancesReport } from '../hooks/useReports.ts';
import { TenantCombobox } from '@/shared/components/TenantCombobox.tsx';
import type { MaintenancesReportParams } from '../api/reports.api.ts';
import type { MaintenanceReportRow } from '@/shared/types/report.ts';

const col = createColumnHelper<MaintenanceReportRow>();

const TIPO_LABELS: Record<string, string> = {
  AGENDADA: 'Agendada',
  RETROATIVA: 'Retroativa',
};

const fmtDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function calcDuration(start: string, end: string): string {
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remMins}min`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

interface TabProps { availableMonths?: string[] }

export function MaintenancesTab({ availableMonths }: TabProps) {
  const MONTH_NOW = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState<PeriodValue>({ mode: 'month', month: MONTH_NOW });
  const [tenantFilter, setTenantFilter] = useState('');
  const [queryParams, setQueryParams] = useState<MaintenancesReportParams | null>(null);
  const [periodError, setPeriodError] = useState('');

  const { data, isLoading } = useMaintenancesReport(queryParams);

  function handleVisualize() {
    setPeriodError('');
    if (period.mode === 'month') {
      if (!period.month) { setPeriodError('Selecione um mes'); return; }
      const [y, m] = period.month.split('-').map(Number);
      const from = new Date(y, m - 1, 1).toISOString();
      const to = new Date(y, m, 0, 23, 59, 59).toISOString();
      setQueryParams({ from, to, tenantId: tenantFilter || undefined });
    } else {
      const err = validatePeriodMax12Months(period);
      if (err) { setPeriodError(err); return; }
      if (!period.from || !period.to) { setPeriodError('Selecione o intervalo completo'); return; }
      setQueryParams({
        from: new Date(period.from).toISOString(),
        to: new Date(period.to).toISOString(),
        tenantId: tenantFilter || undefined,
      });
    }
  }

  const downloadParams: Record<string, string | undefined> = queryParams
    ? { from: queryParams.from, to: queryParams.to, tenantId: queryParams.tenantId }
    : {};

  const columns = useMemo(
    () => [
      col.accessor('tenantNomes', {
        header: 'Tenants Afetados',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <span className="text-gray-400">—</span>;
          const names = val.split(', ').slice(0, 3);
          const rest = val.split(', ').length - names.length;
          return (
            <div className="flex flex-wrap gap-1">
              {names.map((n) => (
                <span key={n} className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {n}
                </span>
              ))}
              {rest > 0 && (
                <span className="inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-600 dark:text-gray-400">
                  +{rest}
                </span>
              )}
            </div>
          );
        },
      }),
      col.accessor('tipo', {
        header: 'Tipo',
        cell: (info) => <span className="text-sm">{TIPO_LABELS[info.getValue()] ?? info.getValue()}</span>,
      }),
      col.accessor('iniciadoEm', {
        header: 'Início (GMT-3)',
        cell: (info) => <span className="text-xs">{fmtDateTime.format(new Date(info.getValue()))}</span>,
      }),
      col.accessor('encerradoEm', {
        header: 'Fim (GMT-3)',
        cell: (info) => <span className="text-xs">{fmtDateTime.format(new Date(info.getValue()))}</span>,
      }),
      col.display({
        id: 'duracao',
        header: 'Duração',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {calcDuration(row.original.iniciadoEm, row.original.encerradoEm)}
          </span>
        ),
      }),
      col.accessor('criadoPor', { header: 'Criado Por' }),
      col.display({
        id: 'modo',
        header: 'Modo',
        cell: ({ row }) => {
          const start = new Date(row.original.iniciadoEm);
          const created = new Date(row.original.iniciadoEm);
          const isRetro = row.original.tipo === 'RETROATIVA' || start < created;
          return (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              isRetro
                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
            }`}>
              {isRetro ? 'Retroativa' : 'Agendada'}
            </span>
          );
        },
      }),
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 pb-2">
        <div className="relative pb-4">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Período</label>
          <PeriodSelector value={period} onChange={setPeriod} modes={['month', 'range']} availableMonths={availableMonths} error={periodError} />
        </div>
        <div className="pb-4 min-w-[220px]">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Tenant</label>
          <TenantCombobox
            value={tenantFilter}
            onChange={(id) => setTenantFilter(id)}
          />
        </div>
        <div className="pb-4">
          <button
            onClick={handleVisualize}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Eye size={14} />
            {isLoading ? 'Carregando...' : 'Visualizar'}
          </button>
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/maintenances/pdf"
            params={downloadParams}
            filename="R002-manutencoes.pdf"
            label="Exportar PDF"
            disabled={!queryParams}
          />
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/maintenances/csv"
            params={downloadParams}
            filename="R002-manutencoes.csv"
            label="Exportar CSV"
            disabled={!queryParams}
          />
        </div>
        {(tenantFilter || queryParams) && (
          <div className="pb-4">
            <button
              onClick={() => {
                setTenantFilter('');
                setPeriod({ mode: 'month', month: MONTH_NOW });
                setQueryParams(null);
                setPeriodError('');
              }}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X size={14} />
              Limpar
            </button>
          </div>
        )}
      </div>

      {queryParams && <ReportTable data={data ?? []} columns={columns} />}
    </div>
  );
}
